import { Fragment, useEffect, useMemo, useState } from 'react';

import { Side } from '@/compiled_proto/com/celertech/marketdata/api/enums/SideProto';
import { Button } from '@/components/common/Button';
import { ConvertOrderFormInput, convertOrderSchema } from '@/components/form/schema/convertSchema';
import { celerCcyName, celerCcyOption, CurrencyOption } from '@/helpers/currencyHelper';
import { shouldUseNopBalance } from '@/helpers/environmentHelper';
import { Modify } from '@/model/common';
import { useAppSelector } from '@/state/hooks';
import { selectAggregatedBalance, selectNopBalance } from '@/state/reducers/balanceSlice';
import { selectShortSellPrevention } from '@/state/reducers/settingSlice';
import { store } from '@/state/store';
import { useCurrency } from '@/utils/hooks/useCurrency';
import { useCurrencyConfigOptions } from '@/utils/hooks/useCurrencyConfigOptions';
import { useDisclosure } from '@/utils/hooks/useDisclosure';
import useLanguage from '@/utils/hooks/useLanguage';
import { Tab } from '@headlessui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import BigNumber from 'bignumber.js';
import cn from 'classnames';
import { FormProvider, useForm } from 'react-hook-form';
import Modal, { ModalProps } from '../Modal';
import ConvertConfirmationModal from './ConvertConfirmationModal';
import { convertToRate } from './ConvertFrom';
import ConvertTab from './ConvertTab';

const emptyConvert = {
    label: '',
    ccy: '',
    value: '',
    celer: ''
};
const availableOption = (options: CurrencyOption[], name: string) => options.find((ccy) => ccy?.value === name);

const defaultValues: Partial<ConvertOrderFormInput> = {
    pair: '',
    bestPrice: undefined,
    convertFrom: emptyConvert,
    convertTo: emptyConvert,
    amount: null,
    estimatedReturn: undefined,
    side: Side.BUY
};

const LiteOrderModal = (props: Modify<ModalProps, { initFrom?: string; initTo?: string }>) => {
    const { opened, handlers, initFrom = '', initTo = '' } = props;

    const nopBalance = useAppSelector(selectNopBalance);
    const celerBalance = useAppSelector(selectAggregatedBalance);
    const shortSellPrevention = useAppSelector(selectShortSellPrevention);

    const lang = useLanguage();

    const convertToDisclosure = useDisclosure(false);
    const confirmationDisclosure = useDisclosure(false);

    const [convertFromState, setConvertFromState] = useState<CurrencyOption>(emptyConvert);

    const { currencyOptions, defaultFrom, defaultTo } = useCurrencyConfigOptions();
    const { min_order_size, order_decimals } = useCurrency(convertFromState?.value);

    const balances = useMemo(() => {
        return shouldUseNopBalance ? nopBalance?.balances || [] : celerBalance;
    }, [nopBalance, celerBalance]);

    const form = useForm<ConvertOrderFormInput>({
        defaultValues,
        mode: 'onChange',
        resolver: yupResolver(
            convertOrderSchema(
                {
                    currencyPair: convertFromState?.value,
                    currencyOut: convertFromState?.value,
                    order_decimals,
                    min_order_size
                },
                shortSellPrevention,
                balances
            )
        )
    });

    const { reset, watch, setValue, handleSubmit, formState } = form;
    const { isValid } = formState;

    const [convertFrom, convertTo, amount] = watch(['convertFrom', 'convertTo', 'amount']) as any;

    const { availableForwardPairs, availableBackwardPairs, availableForwardConverts, availableBackwardConverts } =
        useMemo(() => {
            if (opened) {
                const keys = Object.keys(store.getState().marketPair.bidAsk);
                const celerFrom = convertFrom?.celer || celerCcyName(initFrom);
                const celerTo = convertTo?.celer || celerCcyName(initTo);

                const availableForwardPairs = celerFrom ? keys.filter((key) => key.includes(celerFrom)) : [];
                const availableBackwardPairs = celerTo ? keys.filter((key) => key.includes(celerTo)) : [];
                // console.log({ convertFrom, availableForwardPairs, convertTo, availableBackwardPairs });

                const availableForwardConverts = availableForwardPairs
                    .map((pair) => {
                        const [from, to] = pair.split('/');
                        const ccy = from === celerFrom ? to : from;
                        return celerCcyOption(ccy) || emptyConvert;
                    })
                    .sort((a, b) => a.label.localeCompare(b.label));

                const availableBackwardConverts = availableBackwardPairs
                    .map((pair) => {
                        const [from, to] = pair.split('/');
                        const ccy = to === celerTo ? from : to;
                        return celerCcyOption(ccy) || emptyConvert;
                    })
                    .sort((a, b) => a.label.localeCompare(b.label));

                return {
                    availableForwardPairs,
                    availableBackwardPairs,
                    availableForwardConverts,
                    availableBackwardConverts
                };
            } else {
                return {
                    availableForwardPairs: [],
                    availableBackwardPairs: [],
                    availableForwardConverts: [],
                    availableBackwardConverts: []
                };
            }
        }, [opened, convertFrom?.celer, convertTo?.celer, initFrom, initTo]);

    const { pair, type } = useMemo(() => {
        const linear = `${convertTo?.celer}/${convertFrom?.celer}`;
        const invert = `${convertFrom?.celer}/${convertTo?.celer}`;
        // console.log({ linear, invert });
        // Linear means the pair is available as is, invert means the pair is available in reverse
        if (availableForwardPairs.includes(linear) || availableBackwardPairs.includes(linear))
            return { pair: linear, type: 'ask' };
        else if (availableForwardPairs.includes(invert) || availableBackwardPairs.includes(invert))
            return { pair: invert, type: 'bid' };
        else return { pair: '', type: '' };
    }, [availableForwardPairs, availableBackwardPairs, convertFrom?.celer, convertTo?.celer]);

    const bidAsk = useAppSelector((state) => state.marketPair.bidAsk[pair]);

    const estimatedProceeds = useMemo(() => {
        let returnAmount = 0;
        if (bidAsk) {
            const rate = convertToRate(bidAsk, type);
            returnAmount = BigNumber(rate)
                .multipliedBy(+amount)
                .toNumber();
            setValue('bestPrice', bidAsk[type]);
            setValue('estimatedReturn', returnAmount);
        }
        return returnAmount;
    }, [bidAsk, type, amount]);

    const conversionProps = useMemo(
        () => ({
            availableForwardPairs,
            availableForwardConverts,
            availableBackwardPairs,
            availableBackwardConverts,
            estimatedProceeds,
            settlementDate: bidAsk?.settlementDate,
            pair,
            type
        }),
        [
            availableForwardPairs,
            availableForwardConverts,
            availableBackwardPairs,
            availableBackwardConverts,
            estimatedProceeds,
            bidAsk,
            pair,
            type
        ]
    );

    const onSubmit = () => confirmationDisclosure[1].open();
    const onError = (err) => console.log(err);

    useEffect(() => {
        if (opened) {
            // console.log({ initFrom, initTo });
            let initialFrom: any = defaultFrom;
            let initialTo: any = defaultTo;
            if (initFrom && initTo) {
                initialFrom = availableOption(currencyOptions, initFrom);
                initialTo = availableOption(currencyOptions, initTo);
            } else if (initFrom) {
                initialFrom = availableOption(currencyOptions, initFrom);
                initialTo =
                    availableForwardConverts.find((ccy) => 'BTC' === ccy?.value) ||
                    availableForwardConverts.find((ccy) => 'ETH' === ccy?.value) ||
                    availableForwardConverts.find((ccy) => 'EUR' === ccy?.value) ||
                    availableForwardConverts[0];
                // console.log(initialTo, initialFrom, { availableForwardConverts });
            } else if (initTo) {
                initialTo = availableOption(currencyOptions, initTo);
                initialFrom =
                    availableBackwardConverts.find((ccy) => 'USD' === ccy?.value) ||
                    availableBackwardConverts.find((ccy) => 'USDT' === ccy?.value) ||
                    availableBackwardConverts[0];
                // console.log(initialTo, initialFrom, { availableBackwardConverts });
            }
            const resetValues = {
                ...defaultValues,
                convertFrom: initialFrom || defaultFrom,
                convertTo: initialTo || defaultTo
            };
            setConvertFromState(initialFrom || defaultFrom);
            reset(resetValues);
        } else {
            reset(defaultValues);
            setConvertFromState(emptyConvert);
        }
    }, [opened]);

    return (
        <Fragment>
            <Modal {...props} className="lg:!h-full lg:max-h-[747px]" size="max-w-sm" closeOnOverlay>
                <FormProvider {...form}>
                    <form className="h-full flex flex-col" onSubmit={handleSubmit(onSubmit, onError)}>
                        <div className="h-full flex flex-col w-full text-brand-text">
                            <Tab.Group>
                                <Tab.List className="tab-list overflow-x-auto flex py-2 px-6">
                                    {[lang.lite.convert].map((tab) => (
                                        <Tab
                                            key={tab}
                                            className={({ selected }) =>
                                                cn(
                                                    'p-2 font-semibold text-sm sm:text-base leading-5 text-brand-text focus-visible:outline-none',
                                                    {
                                                        'border-b-[1px] border-b-brand-border': selected,
                                                        'text-brand-neutral-400 hover:brand-primary-light hover:text-brand-text':
                                                            !selected
                                                    }
                                                )
                                            }>
                                            {tab}
                                        </Tab>
                                    ))}
                                </Tab.List>
                                <Tab.Panels className="flex-1 basis-0 overflow-y-auto px-6">
                                    <ConvertTab
                                        balances={balances}
                                        conversionProps={conversionProps}
                                        convertToDisclosure={convertToDisclosure}
                                        setConvertFrom={setConvertFromState}
                                    />
                                </Tab.Panels>
                            </Tab.Group>
                        </div>
                        <div className="flex flex-row justify-between text-brand-text text-sm items-center py-4 px-6">
                            <Button
                                type="submit"
                                disabled={!isValid || !bidAsk}
                                className="w-full rounded-md p-2 px-4 bg-brand-accent hover:bg-brand-accent-light uppercase">
                                {lang.common.review}
                            </Button>
                        </div>
                        <ConvertConfirmationModal
                            conversionProps={conversionProps}
                            opened={confirmationDisclosure[0]}
                            handlers={confirmationDisclosure[1]}
                            onClose={handlers.close}
                        />
                    </form>
                </FormProvider>
            </Modal>
        </Fragment>
    );
};

export default LiteOrderModal;
